import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { 
  addToCart, 
  removeFromCart, 
  updateCartItemQty,
  updateCartItemDiscount,
  applyDiscount,
  clearCart,
  createSaleAsync 
} from './cashierSlice';
import { fetchItemsAsync } from '../inventory/inventorySlice';
import { Item } from '../../types/item';
import { PaymentMethod, SaleCreate, SaleItemCreate, PaymentDetailCreate } from '../../types/sale';
import PaymentModal from './PaymentModal';
import { ReceiptData } from '../../utils/printReceipt';
import ReceiptModal from '../../components/receipt/ReceiptModal';
import api from '../../config/axiosConfig';
import { PRICING_TIER_LABELS } from '../../utils/constants';
import POSViewSwitcher from '../../components/common/POSViewSwitcher';
import CustomerSelector from '../../components/customer/CustomerSelector';
import { Customer } from '../../services/customerService';
import DiscountApprovalModal from '../../components/discount/DiscountApprovalModal';
import { calculateCartWithPricing, type PaymentMethod as PricingPaymentMethod } from '../../utils/cartPricing';
import { getTierDescription } from '../../utils/pricingTier';

const CompletePOSScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { cartItems, subtotal, discount, total, loading } = useSelector((state: RootState) => state.cashier);
  const { items } = useSelector((state: RootState) => state.inventory);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  
  const [barcode, setBarcode] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [customerData, setCustomerData] = useState<Customer | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PricingPaymentMethod>('cash');
  const [paymentDueDays, setPaymentDueDays] = useState<number>(0);
  const [manualDiscount, setManualDiscount] = useState<number>(0);
  const [showDiscountApproval, setShowDiscountApproval] = useState(false);
  const [approvedDiscountBy, setApprovedDiscountBy] = useState<number | null>(null);
  const [approvedDiscountByName, setApprovedDiscountByName] = useState<string>('');
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState<ReceiptData | null>(null);
  
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    // Load items on mount
    dispatch(fetchItemsAsync());
  }, [dispatch]);
  
  useEffect(() => {
    // Auto-focus barcode scanner
    barcodeInputRef.current?.focus();
  }, []);
  
  const handleBarcodeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && barcode.trim()) {
      e.preventDefault();
      handleBarcodeScanned(barcode.trim());
      setBarcode('');
    }
  };
  
  const handleBarcodeScanned = (code: string) => {
    const item = items.find(i => i.barcode === code || i.sku === code);
    if (item) {
      dispatch(addToCart({ item, qty: 1 }));
      // Success feedback
      // Item added to cart successfully
    } else {
      alert(`Item dengan barcode ${code} tidak ditemukan`);
    }
  };
  
  const handleAddToCart = (item: Item) => {
    const stock = item.stock ?? 0;
    if (stock <= 0) {
      alert(`${item.name} habis! Stock: 0`);
      return;
    }
    dispatch(addToCart({ item, qty: 1 }));
  };
  
  const handleRemoveFromCart = (itemId: number) => {
    dispatch(removeFromCart(itemId));
  };
  
  const handleQtyChange = (itemId: number, qty: number) => {
    if (qty > 0) {
      dispatch(updateCartItemQty({ itemId, qty }));
    }
  };
  
  const handleItemDiscountChange = (itemId: number, discount: number) => {
    dispatch(updateCartItemDiscount({ itemId, discount }));
  };
  
  const handleCheckout = () => {
    if (cartItems.length === 0) {
      alert('Keranjang kosong!');
      return;
    }
    
    // Validate gold tier tempo rules
    if (customerData?.pricing_tier === 'gold' && paymentMethod === 'tempo') {
      if (paymentDueDays > 7) {
        alert('⚠️ Gold tier hanya dapat tempo maksimal 7 hari!\n\nDiskon tempo gold: 5%\nDiskon cash gold: 10%');
        return;
      }
    }
    
    // Jika ada diskon manual, minta approval terlebih dahulu
    if (manualDiscount > 0 && !approvedDiscountBy) {
      setShowDiscountApproval(true);
      return;
    }
    
    setShowPayment(true);
  };
  
  const handleDiscountApproval = (approverId: number, approverName: string) => {
    // Simpan info approval
    setApprovedDiscountBy(approverId);
    setApprovedDiscountByName(approverName);
    setShowDiscountApproval(false);
    
    // Apply diskon
    dispatch(applyDiscount(manualDiscount));
    
    // Lanjut ke pembayaran
    setShowPayment(true);
  };
  
  const handleDiscountApprovalCancel = () => {
    setShowDiscountApproval(false);
    // Reset diskon jika approval dibatalkan
    setManualDiscount(0);
  };
  
  const handleManualDiscountChange = (value: number) => {
    setManualDiscount(value);
    // Reset approval jika diskon berubah
    if (approvedDiscountBy) {
      setApprovedDiscountBy(null);
      setApprovedDiscountByName('');
      dispatch(applyDiscount(0)); // Reset diskon di cart
    }
  };
  
  const handlePaymentComplete = async (payments: PaymentDetailCreate[]) => {
    const saleItems: SaleItemCreate[] = cartItems.map(ci => ({
      item_id: ci.item.id,
      qty: ci.qty,
      price: ci.price,
      discount: ci.discount
    }));
    
    // Determine primary payment method
    const primaryPaymentMethod = payments.length > 0 
      ? payments[0].method 
      : PaymentMethod.CASH;
    
    const saleData: SaleCreate = {
      items: saleItems,
      payment_method: primaryPaymentMethod,  // REQUIRED field
      customer_id: selectedCustomer || undefined,
      discount: manualDiscount > 0 ? manualDiscount : discount,
      discount_approved_by: approvedDiscountBy || undefined,
      payment_details: payments.length > 1 ? payments : undefined,  // Only for split payments
      amount_paid: total,
      notes: approvedDiscountByName ? `Diskon approved by: ${approvedDiscountByName}` : undefined,
      customer_name: customerData?.name,
      customer_phone: customerData?.phone,
    };
    
    console.log('📤 Sending sale data:', saleData);
    
    try {
      const result = await dispatch(createSaleAsync({ 
        saleData, 
        user: currentUser, 
        branchId: currentUser?.branch_id 
      })).unwrap();
      console.log('✅ Sale created:', result);
      setShowPayment(false);
      
      // Reset approval state
      setApprovedDiscountBy(null);
      setApprovedDiscountByName('');
      setManualDiscount(0);
      
      // Get receipt data and show modal
      if (result.sale && result.sale.id && result.sale.id > 0) {
        try {
          const receiptResponse = await api.get(`/sales/${result.sale.id}/receipt`);
          const receipt: ReceiptData = receiptResponse.data;
          
          // Enhance receipt with pricing tier information
          if (customerData?.pricing_tier) {
            const cartPricing = calculateCartWithPricing(
              cartItems,
              customerData.pricing_tier,
              paymentMethod,
              paymentDueDays
            );
            
            receipt.customer_pricing_tier = customerData.pricing_tier;
            receipt.pricing_tier_label = PRICING_TIER_LABELS[customerData.pricing_tier];
            receipt.pricing_breakdown = {
              base_total: cartPricing.baseTotal,
              discount_rate: cartPricing.discountRate,
              discount_amount: cartPricing.discountAmount,
              markup_rate: cartPricing.markupRate,
              markup_amount: cartPricing.markupAmount,
              final_total: cartPricing.finalTotal
            };
          }
          
          setReceiptData(receipt);
          setShowReceipt(true);
        } catch (printError) {
          console.error('Error fetching receipt:', printError);
          alert('✅ Transaksi berhasil! (Gagal menampilkan receipt)');
        }
      } else {
        // Offline sale
        alert('✅ Transaksi disimpan offline! Akan disync saat online.');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || error?.message || error;
      console.error('Sale creation error:', error);
      alert(`❌ Transaksi Gagal: ${errorMessage}`);
    }
  };

  const handleCloseReceipt = () => {
    setShowReceipt(false);
    setReceiptData(null);
  };
  
  const filteredItems = items.filter(item => 
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* View Switcher - Floating */}
      <POSViewSwitcher />
      
      {/* Header */}
      <header className="bg-blue-600 text-white p-4 shadow-md">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">POS Kasir</h1>
          <div className="text-sm">
            <span>User: Admin</span> | <span>Cabang: Pusat</span>
          </div>
        </div>
      </header>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Product Grid */}
        <div className="w-2/3 flex flex-col p-4 overflow-hidden">
          {/* Barcode Scanner */}
          <div className="mb-4 bg-white p-4 rounded shadow">
            <label className="block text-sm font-medium mb-2">Scan Barcode</label>
            <input
              ref={barcodeInputRef}
              type="text"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              onKeyDown={handleBarcodeKeyDown}
              placeholder="Scan barcode atau ketik SKU..."
              className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cari produk..."
              className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {/* Product Grid */}
          <div className="flex-1 overflow-y-auto bg-white p-4 rounded shadow">
            <div className="grid grid-cols-3 gap-4">
              {filteredItems.map(item => {
                const stock = item.stock ?? 0; // Default to 0 if stock not available
                return (
                  <div
                    key={item.id}
                    onClick={() => handleAddToCart(item)}
                    className={`border rounded p-3 cursor-pointer transition ${
                      stock > 0 ? 'hover:bg-blue-50 hover:border-blue-300' : 'bg-gray-100 cursor-not-allowed opacity-60'
                    }`}
                  >
                    <div className="font-medium text-sm mb-1 line-clamp-2">{item.name}</div>
                    <div className="text-xs text-gray-500 mb-2">{item.sku}</div>
                    <div className="text-lg font-bold text-blue-600 mb-1">
                      Rp {item.sell_price.toLocaleString()}
                    </div>
                    <div className={`text-xs font-semibold px-2 py-1 rounded ${
                      stock === 0 ? 'bg-red-100 text-red-700' :
                      stock < 5 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      Stock: {stock}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Right: Cart */}
        <div className="w-1/3 bg-white p-4 shadow-lg flex flex-col">
          <h2 className="text-xl font-bold mb-4">Keranjang</h2>
          
          {/* Customer Selector */}
          <CustomerSelector
            selectedCustomerId={selectedCustomer}
            onCustomerSelect={(customerId, customer) => {
              setSelectedCustomer(customerId);
              setCustomerData(customer);
            }}
          />
          
          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto mb-4">
            {cartItems.length === 0 ? (
              <div className="text-center text-gray-400 mt-8">
                Keranjang kosong
              </div>
            ) : (
              <div className="space-y-2">
                {cartItems.map((ci) => (
                  <div key={ci.item.id} className="border rounded p-2">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="font-medium text-sm">{ci.item.name}</div>
                        <div className="text-xs text-gray-500">{ci.item.sku}</div>
                      </div>
                      <button
                        onClick={() => handleRemoveFromCart(ci.item.id)}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        ✕
                      </button>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <label className="text-xs">Qty:</label>
                      <input
                        type="number"
                        min="1"
                        value={ci.qty}
                        onChange={(e) => handleQtyChange(ci.item.id, parseInt(e.target.value))}
                        className="w-16 px-2 py-1 border rounded text-sm"
                      />
                      <span className="text-xs">× Rp {ci.price.toLocaleString()}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <label className="text-xs">Disc:</label>
                      <input
                        type="number"
                        min="0"
                        value={ci.discount}
                        onChange={(e) => handleItemDiscountChange(ci.item.id, parseFloat(e.target.value))}
                        className="w-20 px-2 py-1 border rounded text-sm"
                      />
                    </div>
                    
                    <div className="text-right font-bold">
                      Rp {((ci.price * ci.qty) - ci.discount).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Totals */}
          <div className="border-t pt-4 space-y-2">
            {/* Payment Method Selector (if customer selected) */}
            {customerData && (
              <div className="border rounded p-3 bg-blue-50 mb-3">
                <label className="block text-sm font-medium mb-2">
                  💳 Metode Pembayaran
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value as PricingPaymentMethod)}
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  <option value="cash">💵 Cash</option>
                  <option value="tempo">📅 Tempo</option>
                  <option value="transfer">🏦 Transfer</option>
                </select>
                
                {paymentMethod === 'tempo' && (
                  <div className="mt-2">
                    <label className="block text-xs text-gray-600 mb-1">
                      📆 Jatuh Tempo (hari)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="90"
                      value={paymentDueDays}
                      onChange={(e) => setPaymentDueDays(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border rounded text-sm"
                      placeholder="Masukkan jumlah hari"
                    />
                    {customerData.pricing_tier === 'gold' && paymentDueDays > 7 && (
                      <p className="mt-1 text-xs text-red-500">
                        ⚠️ Gold tier hanya dapat tempo maksimal 7 hari!
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Pricing Display with Tier */}
            {customerData?.pricing_tier ? (
              (() => {
                const cartPricing = calculateCartWithPricing(
                  cartItems,
                  customerData.pricing_tier,
                  paymentMethod,
                  paymentDueDays
                );
                
                return (
                  <div className="border rounded p-3 bg-gradient-to-r from-blue-50 to-purple-50 mb-3">
                    {/* Tier Badge */}
                    <div className="flex items-center gap-2 mb-3 p-2 bg-white rounded-lg border border-blue-200">
                      <span className="text-xl">👤</span>
                      <div className="flex-1">
                        <div className="font-bold text-sm">
                          {PRICING_TIER_LABELS[customerData.pricing_tier]}
                        </div>
                        <div className="text-xs text-gray-600">
                          {getTierDescription(customerData.pricing_tier, paymentMethod, paymentDueDays)}
                        </div>
                      </div>
                    </div>
                    
                    {/* Pricing Breakdown */}
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Harga Normal</span>
                        <span>Rp {cartPricing.baseTotal.toLocaleString()}</span>
                      </div>
                      
                      {cartPricing.markupRate > 0 && (
                        <div className="flex justify-between text-orange-600">
                          <span>Markup (+{(cartPricing.markupRate * 100).toFixed(0)}%)</span>
                          <span>+ Rp {cartPricing.markupAmount.toLocaleString()}</span>
                        </div>
                      )}
                      
                      {cartPricing.discountRate > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Diskon Tier (-{(cartPricing.discountRate * 100).toFixed(0)}%)</span>
                          <span>- Rp {cartPricing.discountAmount.toLocaleString()}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                        <span>TOTAL</span>
                        <span className="text-green-600">
                          Rp {cartPricing.finalTotal.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()
            ) : (
              // Fallback: No customer selected
              <div className="flex justify-between text-sm">
                <span>Subtotal:</span>
                <span>Rp {subtotal.toLocaleString()}</span>
              </div>
            )}
            
            {/* Manual Discount Input */}
            <div className="border rounded p-3 bg-yellow-50">
              <label className="block text-sm font-medium mb-2">
                💰 Diskon Tambahan (Perlu Approval)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max={subtotal}
                  value={manualDiscount}
                  onChange={(e) => handleManualDiscountChange(parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="flex-1 px-3 py-2 border rounded"
                />
                <span className="text-sm font-medium">Rp</span>
              </div>
              {manualDiscount > 0 && approvedDiscountBy && (
                <div className="mt-2 text-xs text-green-600">
                  ✓ Approved by {approvedDiscountByName}
                </div>
              )}
              {manualDiscount > 0 && !approvedDiscountBy && (
                <div className="mt-2 text-xs text-orange-600">
                  ⚠️ Perlu approval Admin/Owner
                </div>
              )}
            </div>
            
            <div className="flex justify-between text-sm">
              <span>Diskon Item:</span>
              <span className="text-red-500">- Rp {discount.toLocaleString()}</span>
            </div>
            
            {manualDiscount > 0 && (
              <div className="flex justify-between text-sm">
                <span>Diskon Manual:</span>
                <span className="text-red-500">- Rp {manualDiscount.toLocaleString()}</span>
              </div>
            )}
            
            <div className="flex justify-between text-xl font-bold border-t pt-2">
              <span>TOTAL:</span>
              <span className="text-green-600">
                Rp {(subtotal - discount - manualDiscount).toLocaleString()}
              </span>
            </div>
          </div>
          
          {/* Actions */}
          <div className="mt-4 space-y-2">
            <button
              onClick={() => dispatch(clearCart())}
              className="w-full py-2 bg-gray-200 hover:bg-gray-300 rounded"
            >
              Clear
            </button>
            <button
              onClick={handleCheckout}
              disabled={cartItems.length === 0 || loading}
              className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : 'BAYAR'}
            </button>
          </div>
        </div>
      </div>
      
      {/* Payment Modal */}
      {showPayment && (
        <PaymentModal
          total={subtotal - discount - manualDiscount}
          onClose={() => setShowPayment(false)}
          onComplete={handlePaymentComplete}
        />
      )}
      
      {/* Discount Approval Modal */}
      {showDiscountApproval && (
        <DiscountApprovalModal
          discountAmount={manualDiscount}
          subtotal={subtotal}
          onApprove={handleDiscountApproval}
          onCancel={handleDiscountApprovalCancel}
        />
      )}
      
      {/* Receipt Modal */}
      {showReceipt && receiptData && (
        <ReceiptModal
          receipt={receiptData}
          isOpen={showReceipt}
          onClose={handleCloseReceipt}
        />
      )}
    </div>
  );
};

export default CompletePOSScreen;
